import type { AutomappingFlagToggleTarget, MappedRoom } from './types';

/**
 * Build automapping targets for batch operations
 */
export const buildAutomappingTargets = (
  rooms: Array<MappedRoom>,
  isEnabled: boolean,
): Array<AutomappingFlagToggleTarget> =>
  rooms.map((room) => ({
    supplierRoomId: room.externalId,
    externalPropertyId: room.externalPropertyId,
    supplier: room.supplier,
    isAutoMappingEnabled: isEnabled,
  }));

/**
 * Compute aggregate automapping state for a group of rooms
 */
export const computeAutomappingState = (
  rooms: Array<MappedRoom>,
  pendingOverrides: App.Bedbank.SupplierRoomAutomappingFlagPayload,
): { checked: boolean; hasOverride: boolean } => {
  const states = rooms.map((room) => {
    const hasPendingChange = pendingOverrides.some(
      (p) => p.supplierRoomId === room.externalId && p.supplier === room.supplier,
    );
    const isEnabled = room.sourceContent?.isAutoMappingEnabled ?? false;
    const checked = isEnabled !== hasPendingChange;
    return { checked, hasPendingChange };
  });

  return {
    checked: states.every((s) => s.checked),
    hasOverride: states.some((s) => s.hasPendingChange),
  };
};

/**
 * Compute verification state for a group of rooms
 */
export const computeVerificationState = (
  rooms: Array<MappedRoom>,
  isPending: (room: MappedRoom) => boolean,
): { allVerified: boolean; someVerified: boolean } => {
  const verified = rooms.map((r) => r.isVerified || isPending(r));
  return {
    allVerified: verified.every(Boolean),
    someVerified: verified.some(Boolean),
  };
};
